/**
 * SmartResponse v1.0.0 — Core
 *
 * Lean core with plugin registry, event system, and action queue.
 * Plugins register via Smart.registerPlugin() from separate files.
 */

import actions from './actions'
import utils from './utils'
import lang from './lang'
import { initClipboard } from './plugins'
import forms, { initDisableInputHandler } from './forms'

// Core infrastructure
import { on, off, emit } from './core/event-system'
import { registerPlugin, initPlugins } from './core/plugin-registry'
import { processActions as queuedProcessActions, setProcessor, markReady } from './core/action-queue'
import { initDataAttributeHandlers } from './core/data-attributes'
import { initTooltips as initTooltipsFn, disposeTooltips as disposeTooltipsFn } from './core/tooltips'

// Ensure Smart exists for early access
window.Smart = window.Smart || {};

let Smart = {
    // State
    initialized: false,
    config: {},

    // Event system
    on: on,
    off: off,
    emit: emit,

    // Plugin registry
    registerPlugin: registerPlugin,

    /**
     * Top-level processActions — public API for flash actions and external callers.
     * Queues if called before init, executes immediately after.
     */
    processActions: queuedProcessActions,

    /**
     * Initialize SmartResponse core.
     */
    start: (config) => {
        if (Smart.initialized) return;
        Smart.initialized = true;

        Smart.config = config || {};

        if (typeof jQuery === 'undefined') {
            console.error('[Smart] jQuery is not loaded!');
            return;
        }

        Smart.console.log('Smart.start(): Init');

        emit('before:init', Smart.config);

        // Set the action processor for the queue
        setProcessor(Smart.utils.process_actions);

        // Core jQuery setup
        Smart.initJQueryValidationDefaults();
        Smart.initAjax();

        $(function () {
            Smart.console.log('Smart.start(): Ready');

            // Initialize data-attribute handlers
            initDataAttributeHandlers();

            // Initialize core plugins (tooltips, clipboard, disableInput)
            Smart.initPlugins();

            // Initialize registered plugins
            initPlugins(Smart.config);

            // Mark action queue as ready — flushes any pending actions
            markReady();

            emit('after:init');
        });
    },

    // AJAX setup
    initAjax: () => {
        $.ajaxSetup({
            headers: {
                'X-CSRF-TOKEN': window.Smart.utils.get_csrf_token(),
            }
        });
    },

    // Tooltip management (delegates to core/tooltips.js helper)
    initTooltips: (root) => initTooltipsFn(root),
    disposeTooltips: (root) => disposeTooltipsFn(root),

    // Plugin init (core plugins like tooltips, clipboard, etc.)
    initPlugins: () => {
        window.Smart.initTooltips();
        window.Smart.initClipboard();
        window.Smart.initDisableInputHandler();
    },

    // Bootstrap customizations
    initBootstrapCustomizations: () => {
        if (window.Smart.utils.is_bootstrap_version_4()) {
            $.fn.dropdown.Constructor.Default.boundary = "window";
            $.fn.modal.Constructor.prototype._enforceFocus = function () {};
        }
    },

    // jQuery Validation defaults
    initJQueryValidationDefaults: () => {
        if (typeof $.validator === 'function') {
            $.validator.setDefaults({
                errorElement: 'span',
                errorClass: 'invalid-feedback',
                errorPlacement: function (error, element) {
                    if (element.parent('.input-group').length ||
                        element.prop('type') === 'checkbox' || element.prop('type') === 'radio') {
                        error.appendTo(element.closest(".form-group"));
                    } else if (element.hasClass('select2-hidden-accessible')) {
                        error.insertAfter(element.next());
                    } else if (element.parent().hasClass('iti')) {
                        error.insertAfter(element.parent());
                    } else {
                        error.insertAfter(element);
                    }
                },
                highlight: function (element) {
                    if ($(element).parent().hasClass('iti')) {
                        $(element).prev().removeClass('is-valid').addClass('is-invalid');
                    } else {
                        $(element).closest('.form-control').removeClass('is-valid').addClass('is-invalid');
                        $(element).closest('.form-group').removeClass('is-valid').addClass('is-invalid');
                    }

                    let tabPane = $(element).closest(".tab-pane");
                    if (tabPane.length > 0) {
                        let tabPaneId = tabPane.attr('id');
                        let tabLi = $('[data-toggle="tab"][href="#' + tabPaneId + '"]').closest('li.nav-item');
                        if (tabPane.find('.is-invalid').length > 0) {
                            tabLi.removeClass('is-valid').addClass('is-invalid');
                        } else {
                            tabLi.removeClass('is-invalid').addClass('is-valid');
                        }
                    }
                },
                ignore: [],
                unhighlight: function (element) {
                    if ($(element).parent().hasClass('iti')) {
                        $(element).prev().removeClass('is-invalid').removeClass('is-valid');
                    } else {
                        $(element).removeClass('is-invalid').removeClass('is-valid');
                    }
                    $(element).closest('.form-control').removeClass('is-invalid is-valid');
                    $(element).closest('.form-group').removeClass('is-invalid is-valid');

                    let tabPane = $(element).closest(".tab-pane");
                    if (tabPane.length > 0) {
                        let tabPaneId = tabPane.attr('id');
                        let tabLi = $('[data-toggle="tab"][href="#' + tabPaneId + '"]').closest('li.nav-item');
                        if (tabPane.find('.is-invalid').length > 0) {
                            tabLi.removeClass('is-valid').addClass('is-invalid');
                        } else {
                            tabLi.removeClass('is-invalid').addClass('is-valid');
                        }
                    }
                },
                success: function (element) {
                    $(element).closest('.form-control').removeClass('is-invalid').addClass('is-valid');
                    $(element).closest('.form-group').removeClass('is-invalid').addClass('is-valid');

                    let tabPane = $(element).closest(".tab-pane");
                    if (tabPane.length > 0) {
                        let tabPaneId = tabPane.attr('id');
                        let tabLi = $('[data-toggle="tab"][href="#' + tabPaneId + '"]').closest('li.nav-item');
                        if (tabPane.find('.is-invalid').length > 0) {
                            tabLi.removeClass('is-valid').addClass('is-invalid');
                        } else {
                            tabLi.removeClass('is-invalid').addClass('is-valid');
                        }
                    }

                    if ($(element).parent().hasClass('iti')) {
                        $(element).prev().removeClass('is-invalid').addClass('is-valid');
                    } else {
                        $(element).removeClass('is-invalid').addClass('is-valid');
                    }
                },
                focusInvalid: false,
                invalidHandler: function (form, validator) {
                    if (!validator.numberOfInvalids()) return;

                    let message = (validator.numberOfInvalids() === 1)
                        ? window.Smart.lang.invalid_single
                        : window.Smart.lang.invalid_multiple.replace('%count%', validator.numberOfInvalids());

                    window.Smart.utils.process_actions([{
                        name: 'toast',
                        data: { type: 'error', title: window.Smart.lang.invalid_title, message }
                    }]);
                },
            });
        } else {
            console.warn('jQuery Validation plugin is not loaded.');
        }
    },

    // Actions
    actions: actions,

    // Utilities
    utils: utils,

    // Forms
    forms: forms,
    initClipboard: initClipboard,
    initDisableInputHandler: initDisableInputHandler,

    // Debug console
    debug: typeof window.Smart.debug === 'boolean' ? window.Smart.debug : false,
    console: {
        log: function (...args) {
            if (!window.Smart.debug) return;
            const trace = (new Error()).stack.split("\n")[2].trim();
            console.log(...args);
            console.log(`    source: ${trace}`);
        },
        info: function (...args) {
            if (!window.Smart.debug) return;
            const trace = (new Error()).stack.split("\n")[2].trim();
            console.info(...args);
            console.log(`    source: ${trace}`);
        },
        error: function (...args) {
            if (!window.Smart.debug) return;
            const trace = (new Error()).stack.split("\n")[2].trim();
            console.error(...args);
            console.log(`    source: ${trace}`);
        },
        table: function (...args) {
            if (!window.Smart.debug) return;
            const trace = (new Error()).stack.split("\n")[2].trim();
            console.table(...args);
            console.log(`    source: ${trace}`);
        },
    },

    // i18n
    lang: lang,
};

// Backwards compatibility alias
Smart.process = Smart.utils.process_actions;

// Make global
window.Smart = Smart;

// Backwards compatibility for global functions
window.insert_browser_history = Smart.utils.insert_browser_history;

// Auto-start on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
    Smart.start();
});

export default Smart;
